import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Stars, StatusBadge, ReviewTrail, ROLE_LABEL } from './reviewParts';
import { formatDateTime } from '../../lib/dateUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminReviews = ({ token, role }) => {
  const [reviews, setReviews] = useState([]);
  const [notes, setNotes] = useState({});
  const waitingStatus = role === 'admin' ? 'with_admin' : 'with_master_admin';

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/reviews`, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(res.data);
    } catch {
      toast.error('Failed to load reviews');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (review, kind) => {
    try {
      await axios.put(`${API_URL}/admin/reviews/${review.review_id}/${kind}`, { note: notes[review.review_id] || '' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(kind === 'handle' ? 'Marked as handled. IT has been told.' : 'Escalated to Master Admin.');
      setNotes({ ...notes, [review.review_id]: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update review');
    }
  };

  const waiting = reviews.filter((r) => r.status === waitingStatus).length;

  return (
    <Card className="shadow-sm" data-testid="admin-reviews-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" /> Reviews sent to you
          {waiting > 0 && <span className="text-xs bg-red-600 text-white rounded-full px-2 py-0.5" data-testid="admin-reviews-waiting-count">{waiting} waiting</span>}
        </CardTitle>
        <CardDescription>
          {role === 'admin' ? 'Mark each review as handled with a note, or escalate it to the Master Admin.' : 'Reviews escalated by Admin. Mark each as handled with a note.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {reviews.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center" data-testid="admin-reviews-empty">No reviews have been sent to you.</p>}
        {reviews.map((r) => {
          const mine = r.status === waitingStatus;
          return (
            <div key={r.review_id} className={`border rounded-xl p-4 space-y-2 ${mine ? 'bg-amber-50/60 border-amber-200' : 'bg-white'}`} data-testid={`admin-review-${r.review_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Stars value={r.rating} size="w-4 h-4" />
                  <span className="text-sm font-medium">{r.author_name}</span>
                  <span className="text-xs text-muted-foreground">{ROLE_LABEL[r.author_role]} · {formatDateTime(r.created_at)}</span>
                </div>
                <StatusBadge status={r.status} testId={`admin-review-status-${r.review_id}`} />
              </div>
              <p className="text-sm">{r.message}</p>
              <ReviewTrail trail={r.trail} />
              {mine && (
                <div className="space-y-2">
                  <Textarea rows={2} placeholder="Note: how was this handled? (needed to mark handled)" value={notes[r.review_id] || ''} onChange={(e) => setNotes({ ...notes, [r.review_id]: e.target.value })} data-testid={`admin-review-note-${r.review_id}`} />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="rounded-full" onClick={() => act(r, 'handle')} data-testid={`admin-review-handle-${r.review_id}`}>Mark handled</Button>
                    {role === 'admin' && (
                      <Button size="sm" variant="outline" className="rounded-full text-purple-700 border-purple-200" onClick={() => act(r, 'escalate')} data-testid={`admin-review-escalate-${r.review_id}`}>Escalate to Master Admin</Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
